// src/components/mediaKit/AsinVideoPicker.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { X, Search, Plus, Check, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { mediaKitApi } from '../../services/mediaKitApi';

const PAGE_SIZE = 50;

// ── simple IndexedDB cache for resolved thumbnails/titles ─────────────
// Keyed by video URL. Once a URL is resolved, we never call resolveLink() again for it.
const DB_NAME = 'msp_media_kit_cache';
const STORE_NAME = 'video_thumbs';

function openDb() {
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, 1);
        req.onupgradeneeded = () => {
            if (!req.result.objectStoreNames.contains(STORE_NAME)) {
                req.result.createObjectStore(STORE_NAME, { keyPath: 'url' });
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}

async function getCachedResolve(url) {
    try {
        const db = await openDb();
        return await new Promise((resolve) => {
            const tx = db.transaction(STORE_NAME, 'readonly');
            const req = tx.objectStore(STORE_NAME).get(url);
            req.onsuccess = () => resolve(req.result || null);
            req.onerror = () => resolve(null);
        });
    } catch {
        return null;
    }
}

async function setCachedResolve(url, data) {
    try {
        const db = await openDb();
        const tx = db.transaction(STORE_NAME, 'readwrite');
        tx.objectStore(STORE_NAME).put({ url, ...data });
    } catch {
        // cache failures are non-fatal — just means we re-fetch next time
    }
}

// Renders a single thumbnail. Falls back to a faded Amazon logo on load failure.
// Keyed by `src` from the parent so it remounts (and resets its own "failed" state)
// the moment the guessed thumbnail is swapped for the real resolved one.
function ThumbImage({ src, asin }) {
    const [failed, setFailed] = useState(false);

    if (failed) {
        return (
            <img
                src="https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg"
                alt={`Product ${asin}`}
                className="w-1/2 h-1/2 object-contain opacity-40"
            />
        );
    }

    return (
        <img
            src={src}
            alt={`Product ${asin}`}
            className="w-full h-full object-contain"
            onLoad={(e) => {
                // Amazon returns a tiny valid placeholder image instead of a 404
                // for missing ASIN photos, so check real dimensions, not just load success.
                if (e.currentTarget.naturalWidth < 20 || e.currentTarget.naturalHeight < 20) {
                    setFailed(true);
                }
            }}
            onError={() => setFailed(true)}
        />
    );
}

export default function AsinVideoPicker({ onClose, onAdd, alreadyAddedUrls = [] }) {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({});
    const [query, setQuery] = useState('');
    const [page, setPage] = useState(1);
    const [added, setAdded] = useState({});

    // url -> { thumbnail_url, title, embed_url, platform }
    const [resolvedMap, setResolvedMap] = useState({});
    // urls currently visible on screen, per IntersectionObserver
    const [visibleUrls, setVisibleUrls] = useState(() => new Set());

    const scrollRef = useRef(null);
    const observerRef = useRef(null);
    const cardElsRef = useRef(new Map()); // url -> element
    const generationRef = useRef(0); // bumped on query/page change to cancel stale queues
    const processingRef = useRef(false);

    useEffect(() => {
        mediaKitApi.getAsinVideos()
            .then((res) => setData(res.asin_counts_with_urls || {}))
            .finally(() => setLoading(false));
    }, []);

    const allRows = useMemo(() => {
        const list = Object.entries(data).flatMap(([asin, info]) =>
            (info.urls || []).map((url) => ({ asin, url }))
        );
        if (!query.trim()) return list;
        const q = query.trim().toLowerCase();
        return list.filter((r) => r.asin.toLowerCase().includes(q) || r.url.toLowerCase().includes(q));
    }, [data, query]);

    useEffect(() => { setPage(1); }, [query]);

    const totalPages = Math.max(1, Math.ceil(allRows.length / PAGE_SIZE));
    const pageRows = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return allRows.slice(start, start + PAGE_SIZE);
    }, [allRows, page]);

    // ── search / filter / page changed → cancel in-flight queue, drop stale visibility tracking ──
    useEffect(() => {
        generationRef.current += 1;
        setVisibleUrls(new Set());
        cardElsRef.current = new Map();
    }, [query, page]);

    // ── set up one IntersectionObserver against the scrollable list ──
    useEffect(() => {
        observerRef.current = new IntersectionObserver(
            (entries) => {
                setVisibleUrls((prev) => {
                    const next = new Set(prev);
                    entries.forEach((entry) => {
                        const url = entry.target.dataset.url;
                        if (entry.isIntersecting) next.add(url);
                        else next.delete(url);
                    });
                    return next;
                });
            },
            { root: scrollRef.current, rootMargin: '200px 0px', threshold: 0.1 }
        );
        return () => observerRef.current?.disconnect();
    }, []);

    // ref-callback: attach/detach each card element to the observer
    const registerCardRef = useCallback((url) => (el) => {
        const observer = observerRef.current;
        const prevEl = cardElsRef.current.get(url);
        if (prevEl && observer) observer.unobserve(prevEl);
        if (el) {
            cardElsRef.current.set(url, el);
            if (observer) observer.observe(el);
        } else {
            cardElsRef.current.delete(url);
        }
    }, []);

    // ── sequential loader: only visible + unresolved urls, one at a time, cache-first ──
    useEffect(() => {
        if (processingRef.current) return;

        const runQueue = async () => {
            processingRef.current = true;
            const myGeneration = generationRef.current;

            const queue = pageRows
                .map((r) => r.url)
                .filter((url) => visibleUrls.has(url) && !resolvedMap[url]);

            for (const url of queue) {
                if (generationRef.current !== myGeneration) break; // search/filter/page changed — stop

                const cached = await getCachedResolve(url);
                if (generationRef.current !== myGeneration) break;

                if (cached) {
                    setResolvedMap((m) => ({ ...m, [url]: cached }));
                    continue;
                }

                try {
                    const resolved = await mediaKitApi.resolveLink(url);
                    if (generationRef.current !== myGeneration) break;
                    setResolvedMap((m) => ({ ...m, [url]: resolved }));
                    setCachedResolve(url, resolved);
                } catch {
                    // leave unresolved — card just keeps showing its current placeholder
                }
            }

            processingRef.current = false;
        };

        runQueue();
    }, [visibleUrls, resolvedMap, pageRows]);

    const handleAdd = async (row) => {
        setAdded((a) => ({ ...a, [row.url]: 'saving' }));
        try {
            // reuse whatever we already resolved (from view or from cache) — never re-fetch
            const resolved = resolvedMap[row.url] || (await getCachedResolve(row.url)) || (await mediaKitApi.resolveLink(row.url));
            if (!resolvedMap[row.url]) setCachedResolve(row.url, resolved);

            await onAdd({
                source_url: row.url,
                platform: 'amazon',
                section_label: 'Amazon Storefront',
                title: resolved.title || `ASIN ${row.asin}`,
                thumbnail_url: resolved.thumbnail_url || `https://images-na.ssl-images-amazon.com/images/P/${row.asin}.01._SCLZZZZZZZ_SX500_.jpg`,
                embed_url: resolved.embed_url, // the .m3u8 stream, if found
                asin: row.asin,
            });
            setAdded((a) => ({ ...a, [row.url]: 'done' }));
        } catch {
            setAdded((a) => ({ ...a, [row.url]: null }));
        }
    };

    return (
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-5 border-b border-slate-200">
                    <div>
                        <h3 className="text-lg font-bold text-slate-800">Add from your Amazon videos</h3>
                        <p className="text-xs text-slate-400">{allRows.length} video{allRows.length === 1 ? '' : 's'} found</p>
                    </div>
                    <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center">
                        <X size={18} />
                    </button>
                </div>

                <div className="p-4 border-b border-slate-200">
                    <div className="relative">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search by ASIN or URL…"
                            className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                        />
                    </div>
                </div>

                <div ref={scrollRef} className="flex-1 overflow-y-auto p-4">
                    {loading && <p className="text-sm text-slate-400 text-center py-8">Loading your videos…</p>}
                    {!loading && pageRows.length === 0 && (
                        <p className="text-sm text-slate-400 text-center py-8">No Amazon videos found.</p>
                    )}
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                        {pageRows.map((row, i) => {
                            const already = alreadyAddedUrls.includes(row.url);
                            const state = added[row.url];
                            const resolved = resolvedMap[row.url];
                            const isResolving = visibleUrls.has(row.url) && !resolved;
                            // fall back to the guessed CDN thumbnail until the real one resolves
                            const thumb = resolved?.thumbnail_url
                                || `https://images-na.ssl-images-amazon.com/images/P/${row.asin}.01._SCLZZZZZZZ_SX300_.jpg`;
                            return (
                                <div
                                    key={`${row.asin}-${i}`}
                                    ref={registerCardRef(row.url)}
                                    data-url={row.url}
                                    className="rounded-xl border border-slate-200 overflow-hidden"
                                >
                                    <a
                                        href={row.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="relative aspect-video bg-white flex items-center justify-center"
                                        title="Open on Amazon"
                                    >
                                        <ThumbImage key={thumb} src={thumb} asin={row.asin} />
                                        {isResolving && (
                                            <span className="absolute bottom-1.5 right-1.5 w-5 h-5 rounded-full bg-black/50 flex items-center justify-center">
                                                <Loader2 size={11} className="animate-spin text-white" />
                                            </span>
                                        )}
                                    </a>
                                    <div className="p-2.5">
                                        <p className="text-[11px] font-bold text-slate-700 truncate">
                                            {resolved?.title || `ASIN: ${row.asin}`}
                                        </p>
                                        <button
                                            disabled={already || state === 'saving' || state === 'done'}
                                            onClick={() => handleAdd(row)}
                                            className={`mt-1.5 w-full inline-flex items-center justify-center gap-1 text-xs font-semibold px-2 py-1.5 rounded-lg transition ${already || state === 'done'
                                                ? 'bg-green-100 text-green-700'
                                                : 'bg-blue-600 text-white hover:bg-blue-700'
                                                }`}
                                        >
                                            {state === 'saving'
                                                ? <Loader2 size={12} className="animate-spin" />
                                                : already || state === 'done'
                                                    ? <><Check size={12} /> Added</>
                                                    : <><Plus size={12} /> Add</>}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {totalPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
                        <button
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={page === 1}
                            className="inline-flex items-center gap-1 text-sm font-semibold px-3 py-1.5 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50"
                        >
                            <ChevronLeft size={14} /> Prev
                        </button>
                        <span className="text-xs text-slate-500 font-medium">Page {page} of {totalPages}</span>
                        <button
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                            className="inline-flex items-center gap-1 text-sm font-semibold px-3 py-1.5 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50"
                        >
                            Next <ChevronRight size={14} />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}